import { LAO_TZ, laoDateParts, parseApiDateTime } from './dateLocal';

// Lao local time root fix (brief 2026-10-09-001): API datetimes are UTC
// (naive, `Z` or `+00:00`); these formatters show them in Lao time whatever
// the browser's zone.
export function formatDate(dateString: string): string {
  const date = parseApiDateTime(dateString);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: LAO_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function formatDateTime(dateString: string): string {
  const date = parseApiDateTime(dateString);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: LAO_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** B4: kept for old callers; English by default, Lao with `lang = 'lo'` (see formatAppRelative). */
export function formatRelativeTime(dateString: string, lang: AppDateLang = 'en'): string {
  return formatAppRelative(dateString, lang);
}

// ---------------------------------------------------------------------------
// B2/B4 (brief 2026-10-11-001): ONE language-aware date/time formatter.
// Always Lao local time (pinned via dateLocal, whatever the browser zone); Lao
// month names on Lao, English on English; always 24 h — never "Oct"/"AM"/"PM"
// on Lao. Built from fixed tables, not the browser's locale data, so every
// browser prints the same thing. Lao names match ICU's `lo` locale.
// ---------------------------------------------------------------------------

export type AppDateLang = 'lo' | 'en';
/** `iso` = `2026-10-09 08:00` (Lao time), language-neutral and sortable — for CSV/XLSX cells. */
export type AppDateStyle = 'datetime' | 'date' | 'time' | 'dateLong' | 'monthYear' | 'weekdayDate' | 'dayMonth' | 'iso';

/** The zone note for an export column header holding `iso` values. */
export const LAO_TIME_NOTE: Record<AppDateLang, string> = { lo: 'ເວລາລາວ, UTC+7', en: 'Lao time, UTC+7' };

const MONTHS_SHORT: Record<AppDateLang, string[]> = {
  lo: ['ມ.ກ.', 'ກ.ພ.', 'ມ.ນ.', 'ມ.ສ.', 'ພ.ພ.', 'ມິ.ຖ.', 'ກ.ລ.', 'ສ.ຫ.', 'ກ.ຍ.', 'ຕ.ລ.', 'ພ.ຈ.', 'ທ.ວ.'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};
const MONTHS_LONG: Record<AppDateLang, string[]> = {
  lo: ['ມັງກອນ', 'ກຸມພາ', 'ມີນາ', 'ເມສາ', 'ພຶດສະພາ', 'ມິຖຸນາ', 'ກໍລະກົດ', 'ສິງຫາ', 'ກັນຍາ', 'ຕຸລາ', 'ພະຈິກ', 'ທັນວາ'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};
const WEEKDAYS: Record<AppDateLang, string[]> = {
  lo: ['ວັນອາທິດ', 'ວັນຈັນ', 'ວັນອັງຄານ', 'ວັນພຸດ', 'ວັນພະຫັດ', 'ວັນສຸກ', 'ວັນເສົາ'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
};

const pad2 = (n: number) => String(n).padStart(2, '0');

function toInstant(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : typeof value === 'number' ? new Date(value) : parseApiDateTime(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * A date/time in Lao local time, in the reader's language.
 * Lao `9 ຕ.ລ. 2026, 08:00` · English `9 Oct 2026, 08:00` (style `datetime`).
 * Accepts an API string (naive = UTC, `Z`, `±HH:MM`, or a plain `YYYY-MM-DD` day), a Date or epoch ms.
 */
export function formatAppDate(
  value: string | number | Date | null | undefined,
  lang: AppDateLang,
  style: AppDateStyle = 'datetime',
  fallback = '—',
): string {
  const d = toInstant(value);
  if (!d) return fallback;
  const p = laoDateParts(d);
  const L: AppDateLang = lang === 'en' ? 'en' : 'lo';
  const short = MONTHS_SHORT[L][p.month - 1];
  const time = `${pad2(p.hour)}:${pad2(p.minute)}`;
  switch (style) {
    case 'date':
      return `${p.day} ${short} ${p.year}`;
    case 'time':
      return time;
    case 'dateLong':
      return `${p.day} ${MONTHS_LONG[L][p.month - 1]} ${p.year}`;
    case 'monthYear':
      return `${MONTHS_LONG[L][p.month - 1]} ${p.year}`;
    case 'weekdayDate':
      return `${WEEKDAYS[L][p.weekday]} ${p.day} ${short} ${p.year}`;
    case 'dayMonth':
      return `${p.day} ${short}`;
    case 'iso':
      return `${p.year}-${pad2(p.month)}-${pad2(p.day)} ${time}`;
    default:
      return `${p.day} ${short} ${p.year}, ${time}`;
  }
}

/** B4: relative while younger than `withinMs`, then the date in `style` — each site keeps its cut-over. */
export function formatAgoOrDate(
  value: string | number | Date | null | undefined,
  lang: AppDateLang,
  withinMs: number,
  style: AppDateStyle,
): string {
  const d = toInstant(value);
  if (!d) return '—';
  return Date.now() - d.getTime() < withinMs ? formatAppRelative(d, lang) : formatAppDate(d, lang, style);
}

/** B4: "x ago" in the reader's language; 30 days or more → the date. */
export function formatAppRelative(value: string | number | Date | null | undefined, lang: AppDateLang): string {
  const d = toInstant(value);
  if (!d) return '—';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  const lo = lang !== 'en';
  if (s < 60) return lo ? 'ຫາກໍ່ນີ້' : 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return lo ? `${m} ນາທີກ່ອນ` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return lo ? `${h} ຊົ່ວໂມງກ່ອນ` : `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return lo ? `${days} ມື້ກ່ອນ` : `${days}d ago`;
  return formatAppDate(d, lo ? 'lo' : 'en', 'date');
}

export function formatCurrency(amount: number, currency = 'LAK'): string {
  // Special handling for Lao Kip (LAK) - use custom format with ₭ symbol
  if (currency === 'LAK') {
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
    return `₭${formatted}`;
  }

  // Default to standard currency formatting for other currencies
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function formatPercentage(value: number | undefined | null, decimals = 1): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '0.0%';
  }
  return `${value.toFixed(decimals)}%`;
}

export function formatDuration(seconds: number | undefined | null): string {
  if (seconds === undefined || seconds === null || isNaN(seconds)) {
    return '0.0s';
  }

  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
}

/**
 * Formats a number with thousand separators.
 * e.g., 5199600 -> "5,199,600"
 *
 * V8.2 P1 UAT FIX: Site-wide thousand separator formatting for currency and large numbers.
 */
export function formatNumber(
  amount: number | string | null | undefined,
  options?: { defaultValue?: string; currency?: string }
): string {
  const { defaultValue = '0', currency } = options || {};
  const num = Number(amount);

  if (isNaN(num)) {
    return defaultValue;
  }

  // If currency option is provided, use currency formatting
  if (currency) {
    return formatCurrency(num, currency);
  }

  // Use Intl.NumberFormat for locale-aware, high-performance formatting.
  return new Intl.NumberFormat('en-US').format(num);
}

/**
 * V17.14.28: Transform static upload URLs to API URLs
 * V17.14.29: Use absolute API URL to bypass CloudFront routing
 * V17.14.30: Handle S3 URLs directly (no transformation needed)
 * V17.14.31: Transform API paths to use API subdomain
 * V17.14.32: Transform old S3 URLs to API endpoint (bucket is private)
 *
 * Document URLs can be in these formats:
 * 1. Legacy local path: /static/uploads/filename.ext -> transform to API endpoint
 * 2. API path: /api/v1/documents/file/... -> prepend API base URL
 * 3. Old S3 URL: https://bucket.s3.region.amazonaws.com/documents/... -> transform to API
 * 4. Scout photo S3 URL: https://bucket.s3.region.amazonaws.com/commercial/scout_leads/... -> use as-is (public)
 * 5. Other full URLs -> use as-is
 *
 * @param url - The document URL
 * @returns Transformed URL or original URL if already valid
 */
export function transformDocumentUrl(url: string | undefined | null): string | undefined {
  if (!url) return undefined;

  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';

  // V17.14.32: Handle S3 URLs - documents/* needs to go through API (private bucket)
  // Scout photos (commercial/scout_leads/*) are public and can be accessed directly
  if (url.includes('.s3.') && url.includes('amazonaws.com')) {
    // Extract the S3 key from the URL
    // https://bucket.s3.region.amazonaws.com/documents/... -> documents/...
    const s3Match = url.match(/amazonaws\.com\/(.+)$/);
    if (s3Match) {
      const s3Key = s3Match[1];
      // Only transform documents/* paths (onboarding docs are private)
      // Scout photos (commercial/scout_leads/*) should remain as-is
      if (s3Key.startsWith('documents/')) {
        return `${apiBaseUrl}/documents/file/${s3Key}`;
      }
    }
    // Scout photos and other S3 content - use as-is
    return url;
  }

  // Other full URLs - use as-is
  if (url.startsWith('https://') || url.startsWith('http://')) {
    return url;
  }

  // V17.14.31: API paths need to be prefixed with API base URL
  // /api/v1/documents/file/... -> https://api.vtwhub.com/api/v1/documents/file/...
  if (url.startsWith('/api/v1/documents/file/')) {
    // Extract the path after /api/v1/
    const pathAfterApi = url.replace('/api/v1/', '');
    return `${apiBaseUrl}/${pathAfterApi}`;
  }

  // V17.14.29: Transform legacy /static/uploads/* URLs to API endpoint
  if (url.startsWith('/static/uploads/')) {
    const filename = url.replace('/static/uploads/', '');
    return `${apiBaseUrl}/documents/file/${filename}`;
  }

  // Return original URL if already using API endpoint or other format
  return url;
}
